import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { useReactToPrint } from 'react-to-print'
import { toast } from 'react-toastify'

import TagCanvas, { checkBarcodeScannability } from './TagCanvas'
import {
	BASE_SIZES, sizeKey, buildPresets, createLayout, normalizeLayout, createElement,
	clampElement, ELEMENT_TYPES, ZOOM_LEVELS, DEFAULT_ZOOM, GRID_MM, round2,
	MIN_LABEL_MM, MAX_LABEL_MM,
} from './layoutModel'
import { MIN_MODULE_MM } from 'helpers/barcode'

const MAX_LOGO_BYTES = 300 * 1024

// Element qo'shish menyusidagi tartib.
const PALETTE = ['name', 'price', 'oldPrice', 'discount', 'wholesalePrice', 'currency',
	'barcode', 'posName', 'uom', 'plu', 'date', 'logo', 'text', 'line']

// Barqaror bo'sh qiymatlar: `|| {}` har renderda yangi obyekt yaratib,
// useMemo/useCallback bog'lanishlarini behuda qayta ishga tushirardi.
const EMPTY_LAYOUTS = {}
const EMPTY_SIZES = []

function TagConstructor({ printProducts, sampleProduct, cashbox }) {
	const { t } = useTranslation()
	const dispatch = useDispatch()

	// Eski foydalanuvchining localStorage'idagi holatda bu kalitlar bo'lmaydi
	// (store preloadedState bilan to'ldiriladi), shuning uchun himoya shart.
	const savedLayouts = useSelector((state) => state.settings.priceTagLayouts) || EMPTY_LAYOUTS
	const savedSizes = useSelector((state) => state.settings.priceTagSizes) || EMPTY_SIZES

	const sizes = useMemo(() => {
		const all = [...BASE_SIZES, ...savedSizes]
		const seen = new Set()
		return all.filter((s) => {
			const key = sizeKey(s.w, s.h)
			if (seen.has(key)) return false
			seen.add(key)
			return true
		})
	}, [savedSizes])

	const [size, setSize] = useState(BASE_SIZES[3])   // 30x20 -- eng ko'p ishlatiladigan
	const [layout, setLayout] = useState(() => createLayout(BASE_SIZES[3].w, BASE_SIZES[3].h))
	const [selectedId, setSelectedId] = useState(null)
	const [zoom, setZoom] = useState(DEFAULT_ZOOM)
	const [dirty, setDirty] = useState(false)
	const [newSize, setNewSize] = useState({ w: '', h: '' })

	const printRef = useRef(null)
	const fileRef = useRef(null)
	const logoRef = useRef(null)

	const key = sizeKey(size.w, size.h)
	const presets = useMemo(() => buildPresets(size.w, size.h), [size.w, size.h])
	const selected = layout.elements.find((el) => el.id === selectedId) || null

	// O'lcham almashganda: saqlangan maket bo'lsa uni, bo'lmasa standart presetni yuklash.
	const loadSize = useCallback((nextSize) => {
		const nextKey = sizeKey(nextSize.w, nextSize.h)
		const saved = normalizeLayout(savedLayouts[nextKey], nextSize.w, nextSize.h)
		setSize(nextSize)
		setLayout(saved || createLayout(nextSize.w, nextSize.h))
		setSelectedId(null)
		setDirty(false)
	}, [savedLayouts])

	// Birinchi ochilishda saqlangan maketni tiklash.
	useEffect(() => {
		const saved = normalizeLayout(savedLayouts[key], size.w, size.h)
		if (saved) setLayout(saved)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const updateElement = (next) => {
		setLayout((prev) => ({
			...prev,
			elements: prev.elements.map((el) => (el.id === next.id ? next : el)),
		}))
		setDirty(true)
	}

	const patchSelected = (patch) => {
		if (!selected) return
		updateElement(clampElement({ ...selected, ...patch }, layout))
	}

	const addElement = (type) => {
		const el = createElement(type, {
			x: round2(size.w * 0.1),
			y: round2(size.h * 0.1),
			w: round2(size.w * 0.6),
			h: round2(size.h * 0.2),
			fontSize: round2(Math.min(Math.max(size.h * 0.12, 1.6), 12)),
		})
		setLayout((prev) => ({ ...prev, elements: [...prev.elements, clampElement(el, prev)] }))
		setSelectedId(el.id)
		setDirty(true)
	}

	const removeElement = (id) => {
		setLayout((prev) => ({ ...prev, elements: prev.elements.filter((el) => el.id !== id) }))
		if (selectedId === id) setSelectedId(null)
		setDirty(true)
	}

	const toggleVisible = (id) => {
		setLayout((prev) => ({
			...prev,
			elements: prev.elements.map((el) => (el.id === id ? { ...el, visible: !el.visible } : el)),
		}))
		setDirty(true)
	}

	const applyPreset = (index) => {
		setLayout(createLayout(size.w, size.h, index))
		setSelectedId(null)
		setDirty(true)
	}

	const save = () => {
		dispatch({
			type: 'SET_PRICE_TAG_LAYOUTS',
			payload: { ...savedLayouts, [key]: layout },
		})
		setDirty(false)
		toast.success(t('SAVED'))
	}

	const addSize = () => {
		const w = Number(newSize.w)
		const h = Number(newSize.h)
		const valid = (n) => Number.isFinite(n) && n >= MIN_LABEL_MM && n <= MAX_LABEL_MM
		if (!valid(w) || !valid(h)) {
			toast.error(`${MIN_LABEL_MM}-${MAX_LABEL_MM} mm`)
			return
		}
		const next = { w: Math.round(w), h: Math.round(h) }
		if (!sizes.some((s) => sizeKey(s.w, s.h) === sizeKey(next.w, next.h))) {
			dispatch({ type: 'SET_PRICE_TAG_SIZES', payload: [...savedSizes, next] })
		}
		setNewSize({ w: '', h: '' })
		loadSize(next)
	}

	// --- STRELKA TUGMALARI BILAN ANIQ SURISH ---------------------------------
	// Sichqoncha bilan 0.5 mm aniqlikda surish qiyin; strelkalar bilan qadamma-qadam.
	useEffect(() => {
		const onKeyDown = (e) => {
			if (!selected) return
			const active = document.activeElement
			if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return

			const step = e.shiftKey ? GRID_MM * 4 : GRID_MM
			const moves = {
				ArrowLeft: { x: selected.x - step },
				ArrowRight: { x: selected.x + step },
				ArrowUp: { y: selected.y - step },
				ArrowDown: { y: selected.y + step },
			}
			if (moves[e.key]) {
				e.preventDefault()
				e.stopPropagation()
				patchSelected(moves[e.key])
			}
			if (e.key === 'Delete' || e.key === 'Backspace') {
				e.preventDefault()
				removeElement(selected.id)
			}
		}
		// capture: Index.js dagi global keydown fokusni qidiruvga o'g'irlab
		// ketishidan oldin ushlab qolamiz.
		document.addEventListener('keydown', onKeyDown, true)
		return () => document.removeEventListener('keydown', onKeyDown, true)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selected, layout])

	// --- EKSPORT / IMPORT -----------------------------------------------------
	// localStorage faqat shu brauzerda qoladi, shuning uchun maketni boshqa
	// kassaga ko'chirish yo'li kerak.
	const exportLayout = () => {
		const blob = new Blob([JSON.stringify(layout, null, 2)], { type: 'application/json' })
		const url = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		a.download = `price-tag-${key}.json`
		a.click()
		URL.revokeObjectURL(url)
	}

	const importLayout = (event) => {
		const file = event.target.files && event.target.files[0]
		if (!file) return
		const reader = new FileReader()
		reader.onload = () => {
			try {
				const parsed = JSON.parse(String(reader.result))
				const next = normalizeLayout(parsed, size.w, size.h)
				if (!next) throw new Error('invalid')
				setLayout(next)
				setSelectedId(null)
				setDirty(true)
				toast.success(t('SUCCESS'))
			} catch (err) {
				toast.error(t('ERROR'))
			}
		}
		reader.readAsText(file)
		event.target.value = ''
	}

	const uploadLogo = (event) => {
		const file = event.target.files && event.target.files[0]
		if (!file || !selected) return
		if (file.size > MAX_LOGO_BYTES) {
			toast.error(`${Math.round(MAX_LOGO_BYTES / 1024)} KB`)
			event.target.value = ''
			return
		}
		const reader = new FileReader()
		reader.onload = () => patchSelected({ src: String(reader.result) })
		reader.readAsDataURL(file)
		event.target.value = ''
	}

	// --- CHOP ETISH -----------------------------------------------------------
	// @page o'lchami yorliqning aynan o'lchami bo'lishi shart: aks holda brauzer
	// A4 ga moslab kichraytiradi va ekrandagi bilan mos kelmaydi.
	const handlePrint = useReactToPrint({
		content: () => printRef.current,
		pageStyle: `
			@page { size: ${size.w}mm ${size.h}mm; margin: 0; }
			@media print {
				html, body { margin: 0; padding: 0; background: #fff; }
				body { -webkit-print-color-adjust: exact; color-adjust: exact; }
			}
		`,
	})

	const scan = checkBarcodeScannability(layout, sampleProduct)
	const canPrint = printProducts.length > 0

	return (
		<div className="tag-constructor">

			{/* O'LCHAMLAR */}
			<div className="d-flex flex-wrap align-items-center gap-2 mb-3">
				<span className="fw-600 me-1">{t('SIZE')}:</span>
				{sizes.map((s) => (
					<button
						key={sizeKey(s.w, s.h)}
						type="button"
						className={'btn btn-sm ' + (sizeKey(s.w, s.h) === key ? 'btn-primary' : 'btn-outline-primary')}
						onClick={() => loadSize(s)}
					>
						{s.w}x{s.h}
					</button>
				))}
				<div className="d-flex align-items-center gap-1 ms-2">
					<input type="number" className="form-control form-control-sm tag-size-input" placeholder={t('WIDTH')}
						value={newSize.w} onChange={(e) => setNewSize({ ...newSize, w: e.target.value })} />
					<span>x</span>
					<input type="number" className="form-control form-control-sm tag-size-input" placeholder={t('HEIGHT')}
						value={newSize.h} onChange={(e) => setNewSize({ ...newSize, h: e.target.value })} />
					<button type="button" className="btn btn-sm btn-outline-primary" onClick={addSize}>+</button>
				</div>
			</div>

			<div className="tag-constructor__body">

				{/* CHAP: KANVAS */}
				<div className="tag-constructor__stage">
					<div className="d-flex justify-content-between align-items-center mb-2">
						<div className="d-flex align-items-center gap-2">
							<span className="text-muted fz12">{t('TEMPLATE')}:</span>
							{presets.map((p, i) => (
								<button key={p.name} type="button" className="btn btn-sm btn-outline-secondary"
									onClick={() => applyPreset(i)}>
									{i + 1}
								</button>
							))}
						</div>
						<div className="d-flex align-items-center gap-1">
							{ZOOM_LEVELS.map((z) => (
								<button key={z} type="button"
									className={'btn btn-sm ' + (z === zoom ? 'btn-secondary' : 'btn-outline-secondary')}
									onClick={() => setZoom(z)}>
									{z}x
								</button>
							))}
						</div>
					</div>

					{/* Kanvas mm o'lchamida, ekranda faqat ko'rish uchun kattalashtiriladi.
					    Geometriya o'zgarmaydi -- shuning uchun chop etish bilan aynan bir xil. */}
					<div className="tag-stage" onMouseDown={(e) => {
						if (e.target === e.currentTarget) setSelectedId(null)
					}}>
						{/* Ramka kattalashtirilgan o'lchamda joy egallaydi: scale() oqimdagi
						    joyni o'zgartirmaydi, shuning uchun ramkasiz yorliq o'z qutisidan
						    chiqib ketardi -- chapda bo'sh joy, o'ngda esa scroll qolardi. */}
						<div
							className="tag-stage__frame"
							style={{
								width: `${size.w * zoom}mm`,
								height: `${size.h * zoom}mm`,
							}}
						>
							<div
								className="tag-stage__scaler"
								style={{
									transform: `scale(${zoom})`,
									width: `${size.w}mm`,
									height: `${size.h}mm`,
								}}
							>
								<TagCanvas
									layout={layout}
									product={sampleProduct}
									cashbox={cashbox}
									edit
									zoom={zoom}
									selectedId={selectedId}
									onSelect={setSelectedId}
									onChange={updateElement}
								/>
							</div>
						</div>
					</div>

					{scan && (
						<div className={'tag-scan-hint ' + (scan.scannable ? 'text-success' : 'text-danger')}>
							{scan.format} · {t('BAR_WIDTH')}: {scan.moduleMm} {t('MM')}
							{scan.scannable
								? ` · ${t('BARCODE_SCANNABLE')}`
								: ` · ${t('BARCODE_TOO_NARROW')} (min ${MIN_MODULE_MM} ${t('MM')})`}
						</div>
					)}
				</div>

				{/* O'NG: ELEMENTLAR VA XOSSALAR */}
				<div className="tag-constructor__side">

					<div className="mb-3">
						<div className="fw-600 mb-1">{t('ELEMENTS')}</div>
						<select className="form-select form-select-sm mb-2" value=""
							onChange={(e) => e.target.value && addElement(e.target.value)}>
							<option value="">+ {t('ADD')}</option>
							{PALETTE.map((type) => (
								<option key={type} value={type}>{t(ELEMENT_TYPES[type].labelKey)}</option>
							))}
						</select>

						<div className="tag-el-list">
							{layout.elements.map((el) => (
								<div key={el.id}
									className={'tag-el-list__row ' + (selectedId === el.id ? 'tag-el-list__row--active' : '')}
									onClick={() => setSelectedId(el.id)}>
									<span className="tag-el-list__name">{t(ELEMENT_TYPES[el.type].labelKey)}</span>
									<button type="button" className="tag-icon-btn" title={t('SHOW')}
										onClick={(e) => { e.stopPropagation(); toggleVisible(el.id) }}>
										{el.visible ? '👁' : '🚫'}
									</button>
									<button type="button" className="tag-icon-btn text-danger" title={t('DELETE')}
										onClick={(e) => { e.stopPropagation(); removeElement(el.id) }}>
										✕
									</button>
								</div>
							))}
						</div>
					</div>

					{selected && (
						<div className="tag-props">
							<div className="fw-600 mb-2">{t(ELEMENT_TYPES[selected.type].labelKey)}</div>

							<div className="row g-1 mb-2">
								{['x', 'y', 'w', 'h'].map((field) => (
									<div className="col-3" key={field}>
										<label className="tag-props__label">{field.toUpperCase()}</label>
										<input type="number" step={GRID_MM} className="form-control form-control-sm"
											value={selected[field]}
											onChange={(e) => patchSelected({ [field]: Number(e.target.value) })} />
									</div>
								))}
							</div>

							{ELEMENT_TYPES[selected.type].kind !== 'line' &&
								ELEMENT_TYPES[selected.type].kind !== 'image' && (
									<div className="row g-1 mb-2">
										<div className="col-6">
											<label className="tag-props__label">{t('FONT_SIZE')} ({t('MM')})</label>
											<input type="number" step="0.1" min="0.5" className="form-control form-control-sm"
												value={selected.fontSize}
												onChange={(e) => patchSelected({ fontSize: Number(e.target.value) })} />
										</div>
										<div className="col-6">
											<label className="tag-props__label">{t('ROTATE')}</label>
											<select className="form-select form-select-sm" value={selected.rotate}
												onChange={(e) => patchSelected({ rotate: Number(e.target.value) })}>
												{[0, 90, 180, 270].map((r) => <option key={r} value={r}>{r}°</option>)}
											</select>
										</div>
									</div>
								)}

							{ELEMENT_TYPES[selected.type].kind === 'text' && (
								<>
									<div className="row g-1 mb-2">
										<div className="col-6">
											<label className="tag-props__label">{t('ALIGN')}</label>
											<select className="form-select form-select-sm" value={selected.align}
												onChange={(e) => patchSelected({ align: e.target.value })}>
												<option value="left">←</option>
												<option value="center">↔</option>
												<option value="right">→</option>
											</select>
										</div>
										<div className="col-6">
											<label className="tag-props__label">{t('LINES')}</label>
											<input type="number" min="1" max="5" className="form-control form-control-sm"
												value={selected.lines}
												onChange={(e) => patchSelected({ lines: Number(e.target.value) })} />
										</div>
									</div>

									<div className="d-flex gap-1 mb-2">
										<button type="button"
											className={'btn btn-sm flex-fill ' + (selected.bold ? 'btn-secondary' : 'btn-outline-secondary')}
											onClick={() => patchSelected({ bold: !selected.bold })}><b>B</b></button>
										<button type="button"
											className={'btn btn-sm flex-fill ' + (selected.italic ? 'btn-secondary' : 'btn-outline-secondary')}
											onClick={() => patchSelected({ italic: !selected.italic })}><i>I</i></button>
										<button type="button"
											className={'btn btn-sm flex-fill ' + (selected.strike ? 'btn-secondary' : 'btn-outline-secondary')}
											onClick={() => patchSelected({ strike: !selected.strike })}><s>S</s></button>
										<button type="button"
											className={'btn btn-sm flex-fill ' + (selected.border ? 'btn-secondary' : 'btn-outline-secondary')}
											onClick={() => patchSelected({ border: !selected.border })}>▢</button>
									</div>

									<div className="row g-1 mb-2">
										<div className="col-6">
											<label className="tag-props__label">{t('PREFIX')}</label>
											<input type="text" className="form-control form-control-sm" value={selected.prefix}
												onChange={(e) => patchSelected({ prefix: e.target.value })} />
										</div>
										<div className="col-6">
											<label className="tag-props__label">{t('SUFFIX')}</label>
											<input type="text" className="form-control form-control-sm" value={selected.suffix}
												onChange={(e) => patchSelected({ suffix: e.target.value })} />
										</div>
									</div>

									{selected.type === 'text' && (
										<div className="mb-2">
											<label className="tag-props__label">{t('TEXT')}</label>
											<input type="text" className="form-control form-control-sm" value={selected.text}
												onChange={(e) => patchSelected({ text: e.target.value })} />
										</div>
									)}
								</>
							)}

							{selected.type === 'barcode' && (
								<div className="form-check mb-2">
									<input type="checkbox" className="form-check-input" id="showBarcodeValue"
										checked={selected.showValue}
										onChange={(e) => patchSelected({ showValue: e.target.checked })} />
									<label className="form-check-label" htmlFor="showBarcodeValue">{t('SHOW_NUMBER')}</label>
								</div>
							)}

							{selected.type === 'logo' && (
								<div className="mb-2">
									<button type="button" className="btn btn-sm btn-outline-primary w-100"
										onClick={() => logoRef.current.click()}>
										{t('UPLOAD')}
									</button>
									<input ref={logoRef} type="file" accept="image/*" className="d-none" onChange={uploadLogo} />
								</div>
							)}
						</div>
					)}

					<div className="tag-actions">
						<button type="button" className={'btn btn-sm w-100 mb-1 ' + (dirty ? 'btn-primary' : 'btn-outline-primary')}
							onClick={save}>
							{t('SAVE')}{dirty ? ' •' : ''}
						</button>
						<div className="d-flex gap-1">
							<button type="button" className="btn btn-sm btn-outline-secondary flex-fill" onClick={exportLayout}>
								{t('EXPORT')}
							</button>
							<button type="button" className="btn btn-sm btn-outline-secondary flex-fill"
								onClick={() => fileRef.current.click()}>
								{t('IMPORT')}
							</button>
							<input ref={fileRef} type="file" accept="application/json" className="d-none" onChange={importLayout} />
						</div>
					</div>
				</div>
			</div>

			{/* CHOP ETISH */}
			<div className="printing">
				<button type="button" className="btn btn-primary w-25 me-2" disabled={!canPrint} onClick={handlePrint}>
					{t('print')} {canPrint ? `(${printProducts.length})` : ''}
				</button>
			</div>

			{/* Chop etiladigan nusxa: AYNAN o'sha TagCanvas, faqat tahrirlash qatlamisiz. */}
			<div className="d-none">
				<div ref={printRef}>
					{printProducts.map((product, index) => (
						<div className="tag-print-page" key={index}
							style={{ width: `${size.w}mm`, height: `${size.h}mm` }}>
							<TagCanvas layout={layout} product={product} cashbox={cashbox} />
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

export default TagConstructor
